/**
 * Text that arrives instead of simply being there — her one visual signature,
 * used only where she performs so it keeps meaning "she is speaking" rather
 * than degrading into "this is a heading".
 *
 * The words fade up one after another at roughly speaking pace, and a single
 * soft bar of light passes over the block once. Both are `transform` and
 * `opacity` on their own layers, which is the whole point of this second
 * version: the first one animated `background-position` on a
 * `background-clip: text` element, so every frame re-rasterised every glyph of
 * a paragraph. It read as a stutter on the phone rather than as a ghost, and
 * the trick it used to keep the text visible if the animation never ran — a
 * discrete `background-image` swap at 99% — flashed on the way out.
 *
 * Nothing here can leave text invisible: a word that never animates is a word
 * at `opacity: 0` only for as long as its `both` fill lasts, and reduced
 * motion drops the whole thing to one plain fade.
 */

import { cn } from '@gramas/ui'
import type { CSSProperties, ElementType } from 'react'
import { Fragment } from 'react'

/** Speaking pace for a short line; a paragraph tightens from here. */
const STEP_MS = 34
const FAST_STEP_MS = 17
/** Past this many words, the slow pace would take four seconds to finish. */
const LONG_WORDS = 14

export interface GhostRevealProps {
  children: string
  /** Start a beat after the line above it, for the second voice on a slide. */
  late?: boolean
  /** Extra wait in ms, when a slide stages more than two voices. */
  delayMs?: number
  /**
   * Send the bar of light across this line as well as arriving it word by
   * word. Reserved for the one headline on a slide: on every paragraph it
   * stops being her and becomes a screen effect.
   */
  sweep?: boolean
  /** Defaults to a span; pass a heading tag where the text is one. */
  as?: ElementType
  className?: string
}

export function GhostReveal({ children, late, delayMs, sweep, as, className }: GhostRevealProps) {
  const Tag = as ?? 'span'
  const words = children.split(' ')
  const step = words.length > LONG_WORDS ? FAST_STEP_MS : STEP_MS
  const delay = (delayMs ?? 0) + (late === true ? 320 : 0)

  return (
    <Tag
      className={cn(sweep === true && 'ghost-sweep', className)}
      style={{ '--d': `${delay}ms`, '--step': `${step}ms` } as CSSProperties}
    >
      {words.map((word, i) => (
        // Index keys: this is one immutable string split on spaces, so a word's
        // position in it is its identity. The space between the spans is a
        // real text node, which is what lets the line wrap normally.
        // biome-ignore lint/suspicious/noArrayIndexKey: see above
        <Fragment key={`${i}-${word}`}>
          {i > 0 && ' '}
          <span className="ghost-word" style={{ '--i': i } as CSSProperties}>
            {word}
          </span>
        </Fragment>
      ))}
    </Tag>
  )
}
